<template>
  <div class="container-xl p-0">
    <div class="register-form registration-page p-3 p-md-4 bg-white">
      <div v-if="!isInitialized">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">{{ $t("Loading…") }}</span>
        </div>
      </div>
      <div v-else>
        <div v-if="registrationNotAvailable && !registration">
          <div class="alert alert-danger">
            {{ $t("Reserving a product is mandatory for registration") }}
          </div>
        </div>
        <RegistrationForm
          v-if="!registrationNotAvailable && isRegistrationFormVisible"
          :account="currentAccount"
          :account-url="accountUrl"
          :account-enabled="accountEnabled"
          :account-required="accountRequired"
          :account-registration-available="accountRegistrationAvailable"
          :fieldsConfig="fieldsConfig"
          :registrationErrors="registrationErrors"
          :optinLabel="optinLabel"
          :termsAndConditionsUrl="termsAndConditionsUrl"
          :with-sidebar="withSidebar"
          :booking="booking"
          :stages="stages"
          :stage="stage"
          :locale="locale"
          :status="status"
          :disabled-registration-form="disableRegistrationForm"
          :disabled-payment-form="disablePaymentForm"
          :disabled-submit="disableSubmitButton"
          :recaptcha-enabled="!!recaptchaSiteKey"
          :tour="tour"
          v-on:submitRegistration="submitRegistration($event)">
          <slot name="registration-form-header"></slot>
          <template v-slot:booking>
            <slot name="registration-booking"
                  :booking="booking"
                  :bookingApiUrl="bookingApiUrl"
                  :on-canceled-booking="onCanceledBooking"
            ></slot>
          </template>
          <template v-slot:login-form>
            <slot name="login-form"></slot>
          </template>
          <template v-slot:create-account>
            <slot name="create-account"></slot>
          </template>
        </RegistrationForm>
        <div v-if="isPhoneConfirmVisible">
          <div v-cloak>
            <div class="row justify-content-center">
              <div class="col-12 col-md-8 mt-5 mb-3">
                <h3>{{ $t("Confirm my phone number") }}</h3>
              </div>
              <div class="col-12 col-md-8 my-2">
                <div class="alert alert-info ">
                  {{ $t("A confirmation code has just been sent to you by message") }}
                </div>
              </div>
              <div class="col-12 col-md-8">
                <form v-on:submit.prevent="onSubmitConfirmPhone">
                  <div class="form-group">
                    <label>{{ $t("Enter your code here to confirm your registration") }}</label>
                    <input type="tel" inputmode="tel" pattern="[0-9]*" ref="phoneConfirmInput" name="phone_code" v-model="phoneCode"
                           class="form-control" :class="{ 'is-invalid': phoneConfirmErrors.phone_code}" placeholder="0000"
                           autocomplete="one-time-code">
                    <div class="invalid-feedback" v-if="phoneConfirmErrors.phone_code">
                      {{ phoneConfirmErrors.phone_code[0] }}
                    </div>
                  </div>

                  <div class="form-group">
                    <button class="btn btn-primary" :disabled="disableButton">
                      {{ $t("Confirm my phone number") }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isRegistrationConfirmVisible">
          <slot name="confirmation"></slot>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, ref, useTemplateRef, watchEffect } from "vue";
import RegistrationForm from "@/components/frontend/RegistrationForm.vue";
import axios from "axios";
import useBookingSession from "@/components/frontend/composable/useBookingSession.js";
import { useI18n } from "vue-i18n";
import { useNotification } from "@kyvg/vue3-notification";
import { useRecaptcha } from "@/composables/useRecaptcha";
import { useGtm } from "@/composables/useGtm";

const { notify } = useNotification();
const { bookingUuid, removeBooking } = useBookingSession();
const { push: pushGtmEvent } = useGtm();
const { t } = useI18n();


const props = defineProps({
  accountEnabled: {
    type: Boolean,
    default: false
  },
  accountRequired: {
    type: Boolean,
    default: false
  },
  accountRegistrationAvailable: {
    type: Boolean,
    default: false
  },
  accountUrl: {
    type: String,
    default: null
  },
  registrationApiUrl: String,
    bookingApiUrl: {
    default: null,
    type: String
  },
  bookingIsRequired: {
    type: Boolean,
    default: false
  },
  confirmPhoneApiUrl: String,
  termsAndConditionsUrl: String,
  fieldsConfig: {
    required: true
  },
  withSidebar: {
    type: Boolean,
    default: false
  },
  optinLabel: String,
    confirmPhone: Boolean,
    locale: String,
  stages: {
    type: Array,
    default: function() {
      return [];
    }
  },
  stage: {
    default: null
  },
  tour: {
    default: null
  },
  recaptchaSiteKey: {
    type: String,
    default: null
  }
})

const { executeRecaptcha } = useRecaptcha(props.recaptchaSiteKey);

const status = ref(null)
const booking = ref(null)
const currentAccount = ref(null)

const registration = ref(null)

const disableRegistrationForm = ref(false)
const disablePaymentForm = ref(false)
const disableSubmitButton = ref(false)

const disableButton = ref(false)
const registrationErrors = ref({})

const phoneCode = ref(null)
const phoneConfirmErrors = ref({})

const openRegisterForm = ref(false)
const isRegistrationFormVisible = ref(true)
const isPhoneConfirmVisible = ref(false)
const isRegistrationConfirmVisible = ref(false)

const registrationNotAvailable = computed(() => {
  return props.bookingIsRequired && !booking.value;
})

const onCanceledBooking = () => {
  removeBooking()
  booking.value = null;
}
const displayRegistrationForm = () => {
  isRegistrationFormVisible.value = true;
  isPhoneConfirmVisible.value = false;
  isRegistrationConfirmVisible.value = false;
}

const phoneConfirmInputRef = useTemplateRef('phoneConfirmInput');
const displayPhoneConfirm = () => {
  isRegistrationFormVisible.value = false;
  isPhoneConfirmVisible.value = true;
  isRegistrationConfirmVisible.value = false;
}
watchEffect(() => {
  if (isPhoneConfirmVisible.value && phoneConfirmInputRef.value) {
    phoneConfirmInputRef.value.focus()
  }
})

const displayRegistrationConfirm = () => {
  isRegistrationFormVisible.value = false;
  isPhoneConfirmVisible.value = false;
  isRegistrationConfirmVisible.value = true;
}

const submitRegistration = async (registrationAndCard) => {
  let registrationToSubmit = registrationAndCard.registration;

  if (disableSubmitButton.value) {
    return;
  }

  disableRegistrationForm.value = true;
  disablePaymentForm.value = true;
  disableSubmitButton.value = true;

  status.value = t("Saving registration…");

  try {
    if (!!registrationToSubmit.uuid) {
      registration.value = registrationToSubmit;
    } else {
      const { data: registrationSaved } = await axios.post(props.registrationApiUrl, {
        ...registrationToSubmit,
        ...{
          lang: props.locale,
          recaptcha_token: await executeRecaptcha("registration")
        }
      });
      registration.value = registrationSaved.registration;
    }

    if (!registration.value.uuid) {
      throw new Error("Registration not saved");
    }

    // Read before removeBooking() clears it.
    const hasBooking = !!(props.bookingApiUrl && bookingUuid.value);

    if (props.bookingApiUrl && registration.value.uuid && bookingUuid.value) {
      const { data: bookingSaved } = await axios.put(props.bookingApiUrl + "/" + bookingUuid.value, {
        registration_uuid: registration.value.uuid
      });
      removeBooking();
    }

    // Legacy event, kept for the containers that already trigger on it.
    pushGtmEvent({ event: "registrationCompleted", registrationUuid: registration.value.uuid });
    // GA4 recommended event. No personal data: the uuid is enough to reconcile with the back office.
    pushGtmEvent({ event: "generate_lead", registration_uuid: registration.value.uuid, has_booking: hasBooking });

    if (props.confirmPhone) {
      displayPhoneConfirm();
    } else {
      displayRegistrationConfirm();
    }

  } catch (error) {
    disableRegistrationForm.value = false;
    disablePaymentForm.value = false;
    disableSubmitButton.value = false;
    status.value = null;

    if (error.response && error.response.data) {
      let message = error.response.data.message;

      if (typeof message === "string" || message instanceof String) {
        notify({
          group: "errors",
          type: "error",
          title: message
        });
      } else if (Object.values(message).length > 0) {

        registrationErrors.value = Object.entries(message).reduce((result, [key, value]) => {
          result[key] = value;
          return result;
        }, {});

        Object.values(message).forEach((error) => {
          notify({
            group: "errors",
            type: "error",
            title: error[0]
          });
        });
      }
    } else if (error.message) {
      notify({
        group: "errors",
        type: "error",
        title: error.message
      });
    }
  }
}

const onSubmitConfirmPhone = () => {
  if (disableButton.value) {
    return;
  }
  disableButton.value = true;
  let data = {
    uuid: registration.value.uuid,
    lang: props.locale,
    phone_code: phoneCode.value,
  };

  axios.post(props.confirmPhoneApiUrl, data).then(function(response) {
    displayRegistrationConfirm();
  }).catch(function(error) {
    if (error.response) {
      phoneConfirmErrors.value = error.response.data.message;
    }
  }).then(function() {
    // always executed
    disableButton.value = false;
  });
}


const isInitialized = ref(false)
onMounted(async () => {
  if (props.accountEnabled) {
    axios.get(props.accountUrl).then(response => {
      currentAccount.value = response.data.data;
    });
  }

  if (props.bookingApiUrl && bookingUuid.value) {
    try {
      const response = await axios.get(props.bookingApiUrl + "/" + bookingUuid.value);
      booking.value = response.data.data;
    } catch (error) {
      removeBooking();
      booking.value = null;
    }
  }

  isInitialized.value = true;
})

</script>
